/**
 * Codes tab -- phase 6b, Task 3. Voucher codes under one promotion.
 *
 * Server-paged DataGrid over GET /promotions/:id/vouchers with active /
 * assigned / code-substring filters. Row actions (write role only) work on
 * ANY voucher kind -- an admin can assign, expire or deactivate a
 * LOYALTY_REWARD or REFERRAL code too (ADMIN_API.md "Vouchers") -- except:
 *  - "New code" / "Generate codes" are MANUAL-only (the API answers 422 for
 *    other kinds, whose codes the engine issues) and hidden otherwise;
 *  - unassigning a LOYALTY_REWARD / REFERRAL_REWARD code goes through a
 *    confirm dialog, because the API clears `redeemableByAssigneeOnly` and
 *    the reward becomes redeemable by anyone who has it;
 *  - a REFERRAL code refuses ANY assignment change (422: the code is the
 *    referrer's identity) -- its Unassign button is rendered disabled with
 *    the same copy the assign dialog shows, and the dialog shows that copy
 *    verbatim if the API ever answers so.
 * Set expiry: datetime-local; "Clear" sends `expiresAt: null`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  AutoAwesome as GenerateIcon,
  ContentCopy as CopyIcon,
  Event as ExpiryIcon,
  PauseCircle as PauseIcon,
  PersonAdd as AssignIcon,
  PersonRemove as UnassignIcon,
  PlayCircle as PlayIcon,
} from '@mui/icons-material';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNotify } from 'react-admin';
import { discountsApi, qk } from '../api';
import { toApiFailure } from '../errors';
import { fmtDateTime } from '../format';
import { gridTestProps } from '../gridTestProps';
import { useCanWriteDiscounts } from '../roles';
import { useDebounced } from '../useDebounced';
import { useScrollToListTop } from '../../../hooks/useScrollToListTop';
import type { ListVouchersQuery, PromotionDetail, VoucherRow } from '../types';
import { isoToLocalInput, localInputToIso } from './PromotionDialog';
import { CreateCodeDialog } from './CreateCodeDialog';
import { BulkCodesDialog } from './BulkCodesDialog';
import { AssignCodeDialog } from './AssignCodeDialog';

type Tri = 'all' | 'yes' | 'no';

/** A reward code that stops being one customer's when unassigned (ADMIN_API.md "Unassigning a reward code"). */
export const isRewardCode = (v: Pick<VoucherRow, 'kind'>): boolean => v.kind === 'LOYALTY_REWARD' || v.kind === 'REFERRAL_REWARD';

export const assigneeName = (u: NonNullable<VoucherRow['assignedUser']>): string => `${u.firstName} ${u.lastName ?? ''}`.trim();

/** Why a REFERRAL code's Unassign is disabled -- the same copy AssignCodeDialog shows up front. */
export const REFERRAL_LOCKED = 'Referral codes belong to the customer who earned them; the engine refuses to reassign them.';

const isExpired = (iso: string | null): boolean => Boolean(iso) && new Date(iso as string).getTime() < Date.now();

type Pending =
  | { kind: 'unassign'; row: VoucherRow }
  | { kind: 'toggle'; row: VoucherRow }
  | { kind: 'expiry'; row: VoucherRow }
  | null;

export interface CodesTabProps {
  promotion: PromotionDetail;
}

export const CodesTab: React.FC<CodesTabProps> = ({ promotion }) => {
  const notify = useNotify();
  const qc = useQueryClient();
  const canWrite = useCanWriteDiscounts();
  const isManual = promotion.kind === 'MANUAL';

  const [active, setActive] = useState<Tri>('all');
  const [assigned, setAssigned] = useState<Tri>('all');
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim(), 300);
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const listTopRef = useScrollToListTop(pagination.page);

  useEffect(() => {
    setPagination((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  }, [active, assigned, q]);

  const query = useMemo<ListVouchersQuery>(
    () => ({
      page: pagination.page + 1,
      limit: pagination.pageSize,
      active: active === 'all' ? undefined : active === 'yes',
      assigned: assigned === 'all' ? undefined : assigned === 'yes',
      q: q || undefined,
    }),
    [pagination, active, assigned, q],
  );

  const list = useQuery({
    queryKey: qk.vouchers(promotion.id, query),
    queryFn: () => discountsApi.listVouchers(promotion.id, query),
    placeholderData: keepPreviousData,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [assignRow, setAssignRow] = useState<VoucherRow | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [expiryInput, setExpiryInput] = useState('');

  const invalidate = () => qc.invalidateQueries({ queryKey: qk.all });
  const fail = (err: unknown) => notify(toApiFailure(err).message, { type: 'error' });

  const unassign = useMutation({
    mutationFn: (row: VoucherRow) => discountsApi.assignVoucher(row.id, { userId: null }),
    onSuccess: async (saved) => {
      await invalidate();
      notify(`${saved.code} unassigned`, { type: 'success' });
    },
    onError: fail,
    onSettled: () => setPending(null),
  });

  const toggle = useMutation({
    mutationFn: (row: VoucherRow) => discountsApi.setVoucherActive(row.id, !row.active),
    onSuccess: async (saved) => {
      await invalidate();
      notify(saved.active ? `${saved.code} reactivated` : `${saved.code} deactivated`, { type: 'success' });
    },
    onError: fail,
    onSettled: () => setPending(null),
  });

  const expiry = useMutation({
    mutationFn: (args: { row: VoucherRow; expiresAt: string | null }) => discountsApi.setVoucherExpiry(args.row.id, args.expiresAt),
    onSuccess: async (saved) => {
      await invalidate();
      notify(saved.expiresAt ? `${saved.code} now expires ${fmtDateTime(saved.expiresAt)}` : `${saved.code} no longer expires`, {
        type: 'success',
      });
    },
    onError: fail,
    onSettled: () => setPending(null),
  });

  // Stable (react-admin's notify and react-query's mutate are both stable),
  // so the columns memo below can list them honestly instead of silencing
  // exhaustive-deps (as PromotionsList's openEdit).
  const copy = useCallback(
    async (code: string) => {
      try {
        await navigator.clipboard.writeText(code);
        notify(`Copied ${code}`, { type: 'info' });
      } catch {
        notify('Copy failed — select the code and copy it by hand.', { type: 'warning' });
      }
    },
    [notify],
  );

  const unassignMutate = unassign.mutate;
  const onUnassign = useCallback(
    (row: VoucherRow) => {
      // Unassigning a MANUAL code is reversible and changes nothing else; a reward code becomes anyone's.
      if (isRewardCode(row)) setPending({ kind: 'unassign', row });
      else unassignMutate(row);
    },
    [unassignMutate],
  );

  const columns = useMemo<GridColDef<VoucherRow>[]>(() => {
    const cols: GridColDef<VoucherRow>[] = [
      {
        field: 'code',
        headerName: 'Code',
        flex: 1.2,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 13 }} noWrap>
              {row.code}
            </Typography>
            <Tooltip title="Copy code">
              <IconButton size="small" aria-label={`Copy ${row.code}`} onClick={() => copy(row.code)}>
                <CopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
      {
        field: 'active',
        headerName: 'Active',
        width: 100,
        renderCell: ({ value }) => <Chip label={value ? 'Active' : 'Inactive'} size="small" color={value ? 'success' : 'default'} />,
      },
      {
        field: 'expiresAt',
        headerName: 'Expires',
        width: 180,
        renderCell: ({ row }) => (
          <Typography variant="body2" color={isExpired(row.expiresAt) ? 'error' : 'text.primary'}>
            {row.expiresAt ? fmtDateTime(row.expiresAt) : 'Never'}
          </Typography>
        ),
      },
      { field: 'timesRedeemed', headerName: 'Redeemed', width: 110, type: 'number' },
      {
        field: 'assignedUser',
        headerName: 'Assigned to',
        flex: 1.4,
        minWidth: 240,
        sortable: false,
        renderCell: ({ row }) =>
          row.assignedUser ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {assigneeName(row.assignedUser)}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {row.assignedUser.email}
                </Typography>
              </Box>
              {row.redeemableByAssigneeOnly && <Chip label="Assignee only" size="small" variant="outlined" color="info" />}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Anyone
            </Typography>
          ),
      },
    ];
    if (canWrite) {
      cols.push({
        field: 'actions',
        headerName: '',
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0}>
            {row.assignedUser ? (
              <Tooltip title={row.kind === 'REFERRAL' ? REFERRAL_LOCKED : 'Unassign'}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Unassign ${row.code}`}
                    disabled={row.kind === 'REFERRAL'}
                    onClick={() => onUnassign(row)}
                  >
                    <UnassignIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="Assign to a customer">
                <IconButton size="small" aria-label={`Assign ${row.code}`} onClick={() => setAssignRow(row)}>
                  <AssignIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Set expiry">
              <IconButton
                size="small"
                aria-label={`Set expiry for ${row.code}`}
                onClick={() => {
                  setExpiryInput(isoToLocalInput(row.expiresAt));
                  setPending({ kind: 'expiry', row });
                }}
              >
                <ExpiryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={row.active ? 'Deactivate' : 'Reactivate'}>
              <IconButton
                size="small"
                aria-label={row.active ? `Deactivate ${row.code}` : `Reactivate ${row.code}`}
                onClick={() => setPending({ kind: 'toggle', row })}
              >
                {row.active ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      });
    }
    return cols;
  }, [canWrite, copy, onUnassign]);

  const busy = unassign.isPending || toggle.isPending || expiry.isPending;
  const closePending = () => (busy ? undefined : setPending(null));

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <TextField
          label="Search"
          placeholder="Code contains…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 40, 'aria-label': 'Search codes' } }}
          sx={{ minWidth: 200 }}
        />
        <ToggleButtonGroup size="small" exclusive value={active} onChange={(_e, v: Tri | null) => v && setActive(v)} aria-label="Active filter">
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="yes">Active</ToggleButton>
          <ToggleButton value="no">Inactive</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={assigned}
          onChange={(_e, v: Tri | null) => v && setAssigned(v)}
          aria-label="Assigned filter"
        >
          <ToggleButton value="all">Any owner</ToggleButton>
          <ToggleButton value="yes">Assigned</ToggleButton>
          <ToggleButton value="no">Unassigned</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        {canWrite && isManual && (
          <>
            <Button variant="outlined" startIcon={<GenerateIcon />} onClick={() => setBulkOpen(true)}>
              Generate codes
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New code
            </Button>
          </>
        )}
      </Stack>

      {list.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load codes: {toApiFailure(list.error).message}
        </Alert>
      )}

      <Box ref={listTopRef} sx={{ width: '100%' }}>
        <DataGrid
          rows={list.data?.data ?? []}
          columns={columns}
          loading={list.isFetching}
          paginationMode="server"
          rowCount={list.data?.total ?? 0}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnMenu
          autoHeight
          getRowHeight={() => 'auto'}
          {...gridTestProps()}
          localeText={{ noRowsLabel: isManual ? 'No codes yet — create one or generate a batch.' : 'No codes match these filters.' }}
          sx={{ '& .MuiDataGrid-cell': { py: 0.75 } }}
        />
      </Box>

      {canWrite && isManual && (
        <>
          <CreateCodeDialog open={createOpen} onClose={() => setCreateOpen(false)} promotionId={promotion.id} />
          <BulkCodesDialog open={bulkOpen} onClose={() => setBulkOpen(false)} promotionId={promotion.id} promotionName={promotion.name} />
        </>
      )}
      {canWrite && <AssignCodeDialog open={assignRow !== null} onClose={() => setAssignRow(null)} voucher={assignRow} />}

      {/* Unassign a reward code: the API clears redeemableByAssigneeOnly, so the code becomes anyone's. */}
      <Dialog open={pending?.kind === 'unassign'} onClose={closePending}>
        <DialogTitle>Unassign {pending?.row.code}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This makes the reward code redeemable by anyone who has it — it is currently restricted to{' '}
            {pending?.row.assignedUser?.email ?? 'its assignee'}. Deactivate the code instead if the aim is to claw the reward back.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" disabled={busy} onClick={() => pending?.kind === 'unassign' && unassign.mutate(pending.row)}>
            Unassign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pending?.kind === 'toggle'} onClose={closePending}>
        <DialogTitle>
          {pending?.row.active ? `Deactivate ${pending?.row.code}?` : `Reactivate ${pending?.row.code}?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pending?.row.active
              ? 'The code stops working at checkout until it is reactivated. Nothing already redeemed changes.'
              : 'The code works at checkout again (subject to the promotion being active, its dates and limits).'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" disabled={busy} onClick={() => pending?.kind === 'toggle' && toggle.mutate(pending.row)}>
            {pending?.row.active ? 'Deactivate' : 'Reactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pending?.kind === 'expiry'} onClose={closePending} fullWidth maxWidth="xs">
        <DialogTitle>Expiry for {pending?.row.code}</DialogTitle>
        <DialogContent>
          <TextField
            id="voucher-expiry"
            label="Expires"
            type="datetime-local"
            value={expiryInput}
            onChange={(e) => setExpiryInput(e.target.value)}
            fullWidth
            disabled={busy}
            helperText="Clear removes the expiry so the code never expires on its own."
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            disabled={busy || !pending?.row.expiresAt}
            onClick={() => pending?.kind === 'expiry' && expiry.mutate({ row: pending.row, expiresAt: null })}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            disabled={busy || !expiryInput.trim()}
            onClick={() => pending?.kind === 'expiry' && expiry.mutate({ row: pending.row, expiresAt: localInputToIso(expiryInput) })}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CodesTab;
